import React, { useState, useEffect } from 'react';
import { FaTwitter, FaInstagram, FaBehance, FaArrowRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <motion.nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isMenuOpen || isScrolled 
            ? 'bg-perspective-carbon/95 backdrop-blur-xl border-b border-white/10 shadow-elevated' 
            : 'bg-transparent'
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ height: '72px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo Section */}
            <motion.div 
              className="flex items-center cursor-pointer group"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="relative">
                <img 
                  src="/perspectivelogo.png" 
                  alt="Perspective HQ" 
                  className="h-9 w-auto transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-perspective-sage/20 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              <NavLink href="/work" currentPath={location.pathname}>Work</NavLink>
              <NavLink href="/services" currentPath={location.pathname}>Services</NavLink>
              <NavLink href="/about" currentPath={location.pathname}>About</NavLink>
              <NavLink href="/blog" currentPath={location.pathname}>Blog</NavLink>
              <div className="mx-2 h-6 w-px bg-white/10" />
              <button 
                onClick={() => navigate('/contact')}
                className="group relative px-6 py-2.5 rounded-full bg-perspective-sage text-perspective-carbon text-sm font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get Started
                  <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-white/20 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden relative z-50 p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 group"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <div className="space-y-1.5 w-6">
                <motion.span 
                  className="block h-0.5 bg-perspective-sage transition-all duration-300"
                  animate={isMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                />
                <motion.span 
                  className="block h-0.5 bg-perspective-sage transition-all duration-300"
                  animate={isMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                />
                <motion.span 
                  className="block h-0.5 bg-perspective-sage transition-all duration-300"
                  animate={isMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                />
              </div>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <motion.div 
              className="fixed inset-y-0 right-0 w-full sm:w-96 bg-perspective-carbon/98 backdrop-blur-2xl z-50 shadow-2xl border-l border-white/10"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ 
                type: "spring", 
                damping: 30, 
                stiffness: 300,
                mass: 0.5
              }}
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div className="flex items-center">
                  <img 
                    src="/perspectivelogo.png" 
                    alt="Perspective HQ" 
                    className="h-8 w-auto"
                  />
                </div>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                >
                  <svg className="w-5 h-5 text-perspective-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Menu Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6">
                  <motion.div 
                    className="space-y-2"
                    initial="closed"
                    animate="open"
                    variants={{
                      open: {
                        transition: { staggerChildren: 0.06, delayChildren: 0.1 }
                      },
                      closed: {
                        transition: { staggerChildren: 0.03, staggerDirection: -1 }
                      }
                    }}
                  >
                    <ModernMobileNavLink 
                      href="/work" 
                      currentPath={location.pathname}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Work
                    </ModernMobileNavLink>
                    <ModernMobileNavLink 
                      href="/services" 
                      currentPath={location.pathname}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Services
                    </ModernMobileNavLink>
                    <ModernMobileNavLink 
                      href="/about" 
                      currentPath={location.pathname}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      About
                    </ModernMobileNavLink>
                    <ModernMobileNavLink 
                      href="/blog" 
                      currentPath={location.pathname}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Blog
                    </ModernMobileNavLink>
                  </motion.div>

                  {/* CTA Section */}
                  <motion.div 
                    className="mt-8 p-6 rounded-2xl border border-perspective-sage/20 bg-perspective-sage/5 backdrop-blur"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <h3 className="text-lg font-semibold text-white mb-2">Ready to get started?</h3>
                    <p className="text-sm text-perspective-cloud/80 mb-4">Let's discuss your project and bring your vision to life.</p>
                    <button 
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/contact');
                      }}
                      className="w-full group bg-perspective-sage text-perspective-carbon py-3 px-6 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg"
                    >
                      <span>Start Your Project</span>
                      <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                    </button>
                  </motion.div>

                  {/* Social Links */}
                  <motion.div 
                    className="mt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <h4 className="text-xs font-semibold text-perspective-cloud/60 uppercase tracking-wider mb-4">Follow Us</h4>
                    <div className="flex gap-3">
                      <a 
                        href="https://twitter.com/perspectivehq" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/5 border border-white/10 hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300 group"
                      >
                        <FaTwitter size={16} className="group-hover:scale-110 transition-transform" />
                      </a>
                      <a 
                        href="https://instagram.com/theperspectivehq" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/5 border border-white/10 hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300 group"
                      >
                        <FaInstagram size={16} className="group-hover:scale-110 transition-transform" />
                      </a>
                      <a 
                        href="https://www.behance.net/skaina" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/5 border border-white/10 hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300 group"
                      >
                        <FaBehance size={16} className="group-hover:scale-110 transition-transform" />
                      </a>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const NavLink = ({ href, children, currentPath }) => {
  const navigate = useNavigate();
  const isActive = currentPath === href || (href !== '/' && currentPath.startsWith(href));
  
  return (
    <button 
      onClick={() => navigate(href)}
      className={`relative text-sm font-medium transition-all duration-300 px-4 py-2.5 rounded-lg group ${
        isActive 
          ? 'text-perspective-sage' 
          : 'text-perspective-cloud/80 hover:text-white'
      }`}
    >
      <span className="relative z-10">{children}</span>
      {isActive && (
        <motion.div
          className="absolute inset-0 bg-perspective-sage/10 rounded-lg border border-perspective-sage/20"
          layoutId="activeNav"
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
        />
      )}
      {!isActive && (
        <div className="absolute inset-0 bg-white/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      )}
    </button>
  );
};

const ModernMobileNavLink = ({ href, children, currentPath, onClick }) => {
  const navigate = useNavigate();
  const isActive = currentPath === href || (href !== '/' && currentPath.startsWith(href));
  
  const handleClick = () => {
    onClick();
    navigate(href);
  };

  return (
    <motion.div
      variants={{
        open: { opacity: 1, x: 0 },
        closed: { opacity: 0, x: 20 }
      }}
    >
      <button 
        onClick={handleClick}
        className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 group ${
          isActive
            ? 'bg-perspective-sage/10 border border-perspective-sage/20 text-perspective-sage'
            : 'hover:bg-white/5 border border-transparent text-perspective-cloud/80 hover:text-white'
        }`}
      >
        <div className="flex-1 text-left">
          <span className="text-base font-semibold">{children}</span>
        </div>
        <div className={`transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          <FaArrowRight className="w-4 h-4 text-perspective-sage group-hover:translate-x-1 transition-transform" />
        </div>
      </button>
    </motion.div>
  );
};

export default Navbar;
